"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { HeroBackdrop } from "@/components/hero-backdrop";
import { Button, ButtonLink } from "@/components/button";
import { Input } from "@/components/input";
import { Select } from "@/components/select";
import {
  LEARNING_PATHS,
  RESOURCE_CATEGORIES,
  RESOURCE_TYPES,
  type ResourceCategory,
  type ResourceItem,
  type ResourceType,
} from "@/lib/content/resourceTaxonomy";
import "./resources.css";

const INITIAL_ARCHIVE_COUNT = 18;

function ResourceArtwork({ item }: { item: ResourceItem }) {
  return item.featuredImageUrl ? (
    <img className="resource-artwork" src={item.featuredImageUrl} alt="" loading="lazy" />
  ) : (
    <div className="resource-artwork resource-artwork--fallback" aria-hidden="true">
      <span>Mapize</span>
    </div>
  );
}

function ResourceMeta({ item }: { item: ResourceItem }) {
  return (
    <div className="resource-meta">
      <span>{item.category}</span>
      <span aria-hidden="true">·</span>
      <span>{item.contentType}</span>
    </div>
  );
}

export function ResourceHub({ items }: { items: ResourceItem[] }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<ResourceCategory | "All">("All");
  const [contentType, setContentType] = useState<ResourceType | "All">("All");
  const [visibleCount, setVisibleCount] = useState(INITIAL_ARCHIVE_COUNT);
  const [showAllFeatured, setShowAllFeatured] = useState(false);

  const featured = items.filter((item) => item.featured).slice(0, 24);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (category === "All" || item.category === category) &&
        (contentType === "All" || item.contentType === contentType) &&
        (!needle ||
          `${item.title} ${item.metaDescription ?? ""} ${item.category} ${item.contentType}`
            .toLowerCase()
            .includes(needle)),
    );
  }, [items, query, category, contentType]);

  const resetVisible = () => setVisibleCount(INITIAL_ARCHIVE_COUNT);

  return (
    <main className="resource-hub">
      <section className="resource-hero">
        <HeroBackdrop />
        <div className="resource-hero__inner">
          <h1>Make better maps, faster.</h1>
          <p className="resource-hero__lede">
            Practical guides, reusable datasets, and interactive examples for turning location data into useful maps.
          </p>
          <div className="resource-search">
            <Input
              id="resource-search"
              type="search"
              value={query}
              placeholder="What do you want to learn?"
              aria-label="Search resources"
              onChange={(event) => {
                setQuery(event.target.value);
                resetVisible();
              }}
            />
          </div>
        </div>
      </section>

      <div className="resource-docs-bridge">
        <span>
          <strong>Need help with the Mapize app?</strong>
          <small>Open Docs for step-by-step guidance on importing, building, sharing, and managing maps.</small>
        </span>
        <ButtonLink href="/help/getting-started/" variant="primary" size="md">
          Read the docs
        </ButtonLink>
      </div>

      <section className="resource-section" aria-labelledby="paths-title">
        <div className="resource-section__heading">
          <div>
            <p className="resource-eyebrow">Mapize Guides</p>
            <h2 id="paths-title">What are you trying to do?</h2>
          </div>
          <p>Choose a workflow and follow a focused set of guides.</p>
        </div>
        <div className="learning-path-grid">
          {LEARNING_PATHS.map((path, index) => {
            const matches = items.filter((item) => path.categories.includes(item.category));
            return (
              <article className="learning-path-card" key={path.id}>
                <span className="learning-path-card__number">{String(index + 1).padStart(2, "0")}</span>
                <h3>{path.title}</h3>
                <p>{path.description}</p>
                <ul>
                  {matches.slice(0, 3).map((item) => (
                    <li key={item.slug}>
                      <Link href={`/${item.slug}/`}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  className="learning-path-card__button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCategory(path.categories[0]!);
                    setContentType("All");
                    setQuery("");
                    resetVisible();
                    document.getElementById("all-resources")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Browse this topic
                </Button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="resource-section" aria-labelledby="featured-title">
        <div className="resource-section__heading">
          <div>
            <p className="resource-eyebrow">Curated resources</p>
            <h2 id="featured-title">Useful places to begin</h2>
          </div>
          <p>Our strongest practical guides and interactive examples.</p>
        </div>
        <div className="featured-resource-grid">
          {featured.slice(0, showAllFeatured ? 24 : 6).map((item) => (
            <article className="featured-resource-card" key={item.slug}>
              <Link href={`/${item.slug}/`} className="featured-resource-card__art">
                <ResourceArtwork item={item} />
              </Link>
              <div className="featured-resource-card__body">
                <ResourceMeta item={item} />
                <h3>
                  <Link href={`/${item.slug}/`}>{item.title}</Link>
                </h3>
                {item.metaDescription ? <p>{item.metaDescription}</p> : null}
              </div>
            </article>
          ))}
        </div>
        {featured.length > 6 ? (
          <div className="featured-resource__footer">
            <button
              type="button"
              className="pz-btn pz-btn--secondary"
              onClick={() => setShowAllFeatured((value) => !value)}
            >
              {showAllFeatured ? "Show fewer recommendations" : "Show all recommendations"}
            </button>
          </div>
        ) : null}
      </section>

      <section className="resource-section resource-library" id="all-resources" aria-labelledby="library-title">
        <div className="resource-section__heading">
          <div>
            <p className="resource-eyebrow">Library</p>
            <h2 id="library-title">All resources</h2>
          </div>
          <p>{filtered.length} matching guides</p>
        </div>
        <div className="resource-filters">
          <label>
            Topic
            <Select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value as ResourceCategory | "All");
                resetVisible();
              }}
            >
              <option value="All">All topics</option>
              {RESOURCE_CATEGORIES.map((value) => <option key={value}>{value}</option>)}
            </Select>
          </label>
          <label>
            Format
            <Select
              value={contentType}
              onChange={(event) => {
                setContentType(event.target.value as ResourceType | "All");
                resetVisible();
              }}
            >
              <option value="All">All formats</option>
              {RESOURCE_TYPES.map((value) => <option key={value}>{value}</option>)}
            </Select>
          </label>
          {(query || category !== "All" || contentType !== "All") && (
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => {
                setQuery("");
                setCategory("All");
                setContentType("All");
                resetVisible();
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {filtered.length ? (
          <>
            <div className="resource-library-grid">
              {filtered.slice(0, visibleCount).map((item) => (
                <article className="resource-library-card" key={item.slug}>
                  <ResourceArtwork item={item} />
                  <div>
                    <ResourceMeta item={item} />
                    <h3><Link href={`/${item.slug}/`}>{item.title}</Link></h3>
                    {item.metaDescription ? <p>{item.metaDescription}</p> : null}
                  </div>
                </article>
              ))}
            </div>
            <div className="resource-library__footer">
              {visibleCount < filtered.length ? (
                <button type="button" className="pz-btn pz-btn--secondary" onClick={() => setVisibleCount((n) => n + 18)}>
                  Show more resources
                </button>
              ) : null}
              <Link href="/resources/page/2/" className="resource-archive-link">
                Browse the paginated archive
              </Link>
            </div>
          </>
        ) : (
          <div className="resource-empty">
            <h3>No matching resources</h3>
            <p>Try a broader search or clear the current filters.</p>
          </div>
        )}
      </section>
    </main>
  );
}
